import React, { useState, useEffect } from 'react';
import supabase from '../integrations/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config.js';

const SupabaseAuthTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const addResult = (test, status, details = '') => {
    setTestResults(prev => [...prev, { test, status, details, timestamp: new Date().toISOString() }]);
  };

  const runTests = async () => {
    setIsLoading(true);
    setTestResults([]);

    try {
      // Test 1: Configuration
      addResult('Configuration', 'info', `URL: ${SUPABASE_URL}, Has Key: ${!!SUPABASE_ANON_KEY}`);

      // Test 2: Client Creation
      try {
        const client = supabase;
        addResult('Client Creation', 'success', 'Supabase client created successfully');
      } catch (error) {
        addResult('Client Creation', 'error', error.message);
      }

      // Test 3: Session Check
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          addResult('Session Check', 'error', error.message);
        } else {
          addResult('Session Check', 'success', data.session ? 'Session found' : 'No session');
        }
      } catch (error) {
        addResult('Session Check', 'error', error.message);
      }

      // Test 4: URL Hash Check
      const hash = window.location.hash;
      if (hash.includes('access_token')) {
        addResult('URL Hash', 'success', 'Access token found in URL hash');
        
        // Parse hash
        try {
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          addResult('Token Parsing', 'success', `Access: ${!!accessToken}, Refresh: ${!!refreshToken}`);
        } catch (error) {
          addResult('Token Parsing', 'error', error.message);
        }
      } else {
        addResult('URL Hash', 'info', 'No access token in URL hash');
      }

      // Test 5: OAuth URL Test
      const currentUrl = window.location.href;
      const isProduction = currentUrl.includes('knoowl.in');
      addResult('Environment', 'info', isProduction ? 'Production' : 'Development');

      // Test 6: Try to set session from URL hash
      if (hash.includes('access_token')) {
        try {
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (accessToken) {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              addResult('Set Session', 'error', error.message);
            } else {
              addResult('Set Session', 'success', 'Session set successfully');
            }
          }
        } catch (error) {
          addResult('Set Session', 'error', error.message);
        }
      }

      // Test 7: Check OAuth configuration
      try {
        const { data: oauthData, error: oauthError } = await supabase.auth.getOAuthUrl({
          provider: 'google',
          options: {
            redirectTo: 'https://www.knoowl.in/auth/callback'
          }
        });
        
        if (oauthError) {
          addResult('OAuth Config', 'error', oauthError.message);
        } else {
          addResult('OAuth Config', 'success', 'OAuth URL generated successfully');
        }
      } catch (error) {
        addResult('OAuth Config', 'error', error.message);
      }

      // Test 8: Check if we can get user from localStorage
      const userToken = localStorage.getItem('user_token');
      const refreshToken = localStorage.getItem('refresh_token');
      addResult('LocalStorage', 'info', `User Token: ${!!userToken}, Refresh Token: ${!!refreshToken}`);

    } catch (error) {
      addResult('General', 'error', error.message);
    }

    setIsLoading(false);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Supabase Auth Test</h2>
      
      <button 
        onClick={runTests}
        disabled={isLoading}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
      >
        {isLoading ? 'Running Tests...' : 'Run Auth Tests'}
      </button>

      <div className="space-y-2">
        {testResults.map((result, index) => (
          <div key={index} className={`p-3 rounded border ${
            result.status === 'success' ? 'bg-green-50 border-green-200' :
            result.status === 'error' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <div className="font-semibold">{result.test}</div>
            <div className="text-sm text-gray-600">{result.details}</div>
            <div className="text-xs text-gray-400">{result.timestamp}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SupabaseAuthTest; 